import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Box, Button, Typography, IconButton, Drawer, List, ListItem } from '@mui/material';
import { styled } from '@mui/material/styles';
import HomeIcon from '@mui/icons-material/Home';
import ChatIcon from '@mui/icons-material/Chat';
import DateRangeIcon from '@mui/icons-material/DateRange';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import FavoriteIcon from '@mui/icons-material/Favorite';
import PersonIcon from '@mui/icons-material/Person';
import PeopleIcon from '@mui/icons-material/People';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationBell from './NotificationBell';

const NavBar = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  background: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(10px)',
  borderBottom: '1px solid rgba(255, 105, 180, 0.2)',
  zIndex: 1000,
  padding: theme.spacing(1, 2),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  boxShadow: '0 2px 20px rgba(255, 105, 180, 0.1)',
}));

const NavLinks = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  alignItems: 'center',
  [theme.breakpoints.up('md')]: {
    gap: theme.spacing(2),
  },
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  }
}));

const MobileMenuButton = styled(IconButton)(({ theme }) => ({
  display: 'none',
  color: 'var(--gradient-start)',
  [theme.breakpoints.down('sm')]: {
    display: 'flex',
  }
}));

const NavButton = styled(Button)(({ theme }) => ({
  color: 'white',
  borderRadius: 12,
  padding: theme.spacing(0.75, 2),
  textTransform: 'none',
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  background: 'linear-gradient(90deg, var(--gradient-start), var(--gradient-end))',
  boxShadow: '0 8px 20px rgba(123,97,255,0.12)',
  '&:hover': {
    transform: 'translateY(-3px)',
  },
  transition: 'all 0.18s ease',
  '& .MuiSvgIcon-root': {
    fontSize: '1.2rem',
  },
  [theme.breakpoints.down('md')]: {
    minWidth: 'unset',
    padding: theme.spacing(1),
    '& .button-text': {
      display: 'none',
    }
  }
}));

const Logo = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  fontSize: '1.5rem',
  background: 'linear-gradient(45deg, #FF69B4 0%, #8E37D7 100%)',
  background: 'linear-gradient(45deg, var(--gradient-start) 0%, var(--gradient-end) 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  cursor: 'pointer',
  '&:hover': {
    transform: 'scale(1.05)',
  },
  transition: 'transform 0.3s ease',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.2rem',
    whiteSpace: 'nowrap',
  }
}));

const MobileNavItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(2),
  '&:hover': {
    backgroundColor: 'rgba(123,97,255,0.06)',
  }
}));

const Navigation = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
      setMobileOpen(false);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const mobileMenu = (
    <Box sx={{ width: 250 }} onClick={handleDrawerToggle}>
      <List>
        <MobileNavItem component={Link} to="/memories">
          <HomeIcon sx={{ mr: 2, color: 'var(--gradient-start)' }} />
          <Typography>Memories</Typography>
        </MobileNavItem>
        <MobileNavItem component={Link} to="/chat">
          <ChatIcon sx={{ mr: 2, color: 'var(--gradient-start)' }} />
          <Typography>Chat</Typography>
        </MobileNavItem>
        <MobileNavItem component={Link} to="/calendar">
          <DateRangeIcon sx={{ mr: 2, color: 'var(--gradient-start)' }} />
          <Typography>Calendar</Typography>
        </MobileNavItem>
        <MobileNavItem component={Link} to="/users">
          <PeopleIcon sx={{ mr: 2, color: 'var(--gradient-start)' }} />
          <Typography>Users</Typography>
        </MobileNavItem>
        <MobileNavItem component={Link} to="/profile">
          <PersonIcon sx={{ mr: 2, color: 'var(--gradient-start)' }} />
          <Typography>Profile</Typography>
        </MobileNavItem>
        <MobileNavItem component={Link} to="/settings">
          <SettingsIcon sx={{ mr: 2, color: 'var(--gradient-start)' }} />
          <Typography>Settings</Typography>
        </MobileNavItem>
        <MobileNavItem onClick={handleLogout}>
          <LogoutIcon sx={{ mr: 2, color: 'var(--gradient-start)' }} />
          <Typography>Logout</Typography>
        </MobileNavItem>
      </List>
    </Box>
  );

  if (!user) {
    return (
      <NavBar>
        <Logo component={Link} to="/">
          <FavoriteIcon sx={{ color: 'var(--gradient-start)' }} />
          Amour Space
        </Logo>
        <NavLinks>
          <NavButton component={Link} to="/login">
            Login
          </NavButton>
        </NavLinks>
      </NavBar>
    );
  }

  return (
    <>
      <NavBar>
        <Logo component={Link} to="/memories">
          <FavoriteIcon sx={{ color: 'var(--gradient-start)' }} />
          Amour Space
        </Logo>
        <NavLinks>
          <NavButton component={Link} to="/memories">
            <HomeIcon />
            <span className="button-text">Memories</span>
          </NavButton>
          <NavButton component={Link} to="/chat">
            <ChatIcon />
            <span className="button-text">Chat</span>
          </NavButton>
          <NavButton component={Link} to="/calendar">
            <DateRangeIcon />
            <span className="button-text">Calendar</span>
          </NavButton>
          <NavButton component={Link} to="/users">
            <PeopleIcon />
            <span className="button-text">Users</span>
          </NavButton>
          <NavButton component={Link} to="/profile">
            <PersonIcon />
            <span className="button-text">Profile</span>
          </NavButton>
          <NavButton component={Link} to="/settings">
            <SettingsIcon />
            <span className="button-text">Settings</span>
          </NavButton>
          <NotificationBell />
          <NavButton onClick={handleLogout}>
            <LogoutIcon />
            <span className="button-text">Logout</span>
          </NavButton>
        </NavLinks>
        <MobileMenuButton 
          aria-label="open menu"
          onClick={handleDrawerToggle}
        >
          <MenuIcon />
        </MobileMenuButton>
      </NavBar>
      
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        sx={{
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box',
            width: 250,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
          },
        }}
      >
        {mobileMenu}
      </Drawer>
    </>
  );
};

export default Navigation; 